import {
  ExternalCollection,
  FetchParams,
} from "@bitwarden/web-vault/app/services/store/external.collection.abstraction";
import { ForexRateView } from "@bitwarden/web-vault/app/models/view/reference-data/forex-rate.view";
import { inject, signal } from "@angular/core";
import { addDays, differenceInCalendarDays, format } from "date-fns";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { MarketDataConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { DataRepositoryService } from "@bitwarden/web-vault/app/services/DataRepository/data-repository.service";

interface FetchCurrencyParams extends FetchParams {
  baseCurrency: string;
  currencies: string[];
  startDate: Date;
  endDate: Date;
}

const MARKET_DATA_CONFIG = process.env.MARKETDATA as MarketDataConfigType;
const API_DATE_FORMAT = "yyyy-MM-dd";
/** Maximum number of days per request - larger ranges are split into chunks. */
const MAX_FETCH_RANGE_DAYS = 90;

/**
 * Splits [startDate, endDate] into chunks of at most chunkSize calendar days. Uses
 * differenceInCalendarDays (not raw timestamp comparisons) so a mismatched time-of-day
 * between startDate and endDate can't cause the final day to be dropped.
 */
function chunkDateRange(
  startDate: Date,
  endDate: Date,
  chunkSize: number,
): { startDate: Date; endDate: Date }[] {
  const chunks: { startDate: Date; endDate: Date }[] = [];
  const totalDays = differenceInCalendarDays(endDate, startDate);

  for (let offset = 0; offset <= totalDays; offset += chunkSize) {
    const chunkEndOffset = offset + chunkSize - 1;
    chunks.push({
      startDate: addDays(startDate, offset),
      endDate: chunkEndOffset >= totalDays ? endDate : addDays(startDate, chunkEndOffset),
    });
  }

  return chunks;
}

export class ExternalForexRatesStoreCollection extends ExternalCollection {
  protected log: LogService = inject(LogService);
  protected appState = inject(AppStateStoreService);
  protected dataRepositoryService: DataRepositoryService = inject(DataRepositoryService);

  /** When pulling currency we also need to know the transaction period interval **/

  private _forexRates = signal<ForexRateView[]>(null);
  readonly forexRates = this._forexRates.asReadonly();

  clear() {
    this._forexRates.set(null);
  }

  private validateParams(params: FetchCurrencyParams) {
    /** Check params validity **/
    if (
      typeof params?.baseCurrency === "undefined" ||
      typeof params?.currencies === "undefined" ||
      typeof params?.startDate === "undefined" ||
      typeof params?.endDate === "undefined"
    ) {
      return false;
    }

    return differenceInCalendarDays(params.endDate, params.startDate) >= 0;
  }

  async fetch(params: FetchCurrencyParams): Promise<ForexRateView[]> {
    const forexRates: ForexRateView[] = [];

    if (!this.validateParams(params)) {
      return forexRates;
    }

    /** Remove self cross rate e.g AUD to AUD, and validate that there are some rates to fetch  **/
    const symbols = params.currencies.filter((s) => s !== params.baseCurrency).join(",");
    if (symbols === "") {
      return forexRates;
    }

    try {
      this.appState.startProcess("fetching-forex", "Fetching Currency Rates...");

      // large ranges are split into chunks to stay under the API's per-request limit; chunks are
      // independent requests, so they're fetched concurrently rather than one-by-one.
      // TODO: unbounded fan-out for long ranges (years of history -> dozens of simultaneous
      // requests) increases the odds of hitting the API's rate limit and triggering the
      // truncation below. Consider capping concurrency (batches) or adding retry/backoff.
      const dateRanges = chunkDateRange(params.startDate, params.endDate, MAX_FETCH_RANGE_DAYS);
      const results = await Promise.allSettled(
        dateRanges.map((chunk) => {
          const path = `?base=${params.baseCurrency}&startDate=${format(chunk.startDate, API_DATE_FORMAT)}&endDate=${format(chunk.endDate, API_DATE_FORMAT)}&symbols=${symbols}`;
          return this.dataRepositoryService.send(
            "GET",
            path,
            null,
            true,
            true,
            `${MARKET_DATA_CONFIG.url}/${MARKET_DATA_CONFIG.apiStage}${MARKET_DATA_CONFIG.endpoint?.currency}`,
          );
        }),
      );

      // Persist only the contiguous run of successful chunks: stop at the first failure so a failed
      // chunk becomes a *trailing* gap (which recalcDateRange refetches next run) rather than an
      // *interior* gap the missing-range logic can't see and would never backfill.
      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        if (result.status !== "fulfilled") {
          const chunk = dateRanges[i];
          this.log.warning(
            `Failed to fetch [${format(chunk.startDate, API_DATE_FORMAT)} to ${format(chunk.endDate, API_DATE_FORMAT)}]: ${result.reason}`,
          );
          break;
        }
        if (Array.isArray(result.value?.referenceData)) {
          result.value.referenceData.forEach((item: ForexRateView) => {
            forexRates.push(this.mapToView(item));
          });
        }
      }
      this.log.info(`Fetched ${forexRates.length} forex rate rows`);

      // The leading chunk failing outright means no forward progress can be made this run:
      // surface the error the way the single pre-chunking request used to, so the caller's catch
      // runs and cached rates aren't clobbered with []. An empty-but-successful leading chunk is
      // NOT a failure - it just means that range legitimately had no rates.
      if (results[0]?.status === "rejected") {
        throw new Error(`Failed to fetch forex rates: ${results[0].reason}`);
      }

      this._forexRates.set(forexRates);
    } finally {
      this.appState.endProcess("fetching-forex");
    }

    return forexRates;
  }

  protected mapToView(data: any): ForexRateView {
    return new ForexRateView(data);
  }
  triggerChanges(): void {}
}
